import { useCallback, useMemo } from "react";
import { useDocumentViewerUrlQuery } from "#graphql/supergraphHooks";
import type { ApolloError } from "@apollo/client";

/** Resolved viewer URL view-model the PDF wrapper renders; GraphQL-agnostic. */
export type DocumentViewerUrl = {
  /** Short-lived (~300s) presigned PDF URL from DDG, proxied by fay. */
  pdfUrl: string;
  filename: string;
  contentType: string;
};

export type DocumentViewerUrlData = {
  viewer?: DocumentViewerUrl;
  loading: boolean;
  error?: ApolloError;
  /** Re-fetches a fresh presigned URL; stable so `PdfPanel` keeps its refresh timer. */
  refresh: () => void;
};

/**
 * Fetches a single document's presigned PDF URL via fay's `documentViewerUrl`
 * and maps it to a flat view-model. `skip`s until a package document is selected
 * and a valid arena resolves so the closed viewer issues no query.
 *
 * Page navigation is deliberately client-side (`PdfNavigationProvider`'s
 * `navigateToPage`), NOT a query variable — so section-to-section jumps within
 * the same document don't re-fetch a new presigned URL and re-mount the iframe.
 * The URL is stable per document; the reader scrolls within the loaded iframe.
 *
 * The presigned URL has a ~300s TTL, so this reads `network-only`: a cached URL
 * could be expired by the time the viewer reopens. In-session expiry is handled
 * separately by `PdfPanel`'s refresh timer, which calls `refresh` (this query's
 * `refetch`) ahead of the deadline.
 *
 * @param arenaId The read is arena-scoped server-side (fay requires arenaId);
 * undefined until the host app resolves its arena, which skips the query so the
 * required arg is never issued as NaN.
 */
export function useDocumentViewerUrl(
  documentId: string | null,
  arenaId: number | undefined,
  options?: { documentVersion?: number },
): DocumentViewerUrlData {
  const { data, loading, error, refetch } = useDocumentViewerUrlQuery({
    variables: {
      arenaId: arenaId!,
      documentId: documentId ?? "",
      documentVersion: options?.documentVersion,
    },
    skip: !documentId || arenaId === undefined,
    fetchPolicy: "network-only",
  });

  // `refetch` is already stable per observable query, but wrap it to a bare
  // `() => void` so `PdfPanel` (memoized) gets a stable `onRefreshUrl` identity
  // and never re-arms its refresh timer on a parent re-render. A rejected
  // `refetch` (network error) is already surfaced through `error`, so swallow
  // the Promise here to avoid an unhandled rejection.
  const refresh = useCallback(() => {
    Promise.resolve(refetch()).catch(() => undefined);
  }, [refetch]);

  const viewer = useMemo<DocumentViewerUrl | undefined>(() => {
    const node = data?.documentViewerUrl;
    if (!node) {
      return undefined;
    }
    return {
      pdfUrl: node.pdfUrl,
      filename: node.filename,
      contentType: node.contentType,
    };
  }, [data]);

  return { viewer, loading, error, refresh };
}
